/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * PresenceControls.tsx — Presenc\1 \2ontrols (services/operator-web-app).
 */

import { useOperatorStore } from '@/store';
import { buildPresencePause, buildPresenceResume } from '@/realtime/commands';
import type { PauseReason } from '@/realtime/commands';
import { Button, Badge } from '@/components/ui';
import { useTranslation } from '@/i18n';

export function PresenceControls() {
  const t = useTranslation();
  const presence = useOperatorStore((s) => s.presence);
  const connectionState = useOperatorStore((s) => s.connectionState);
  const send = useOperatorStore((s) => s.send);

  const isConnected = connectionState === 'connected';
  const isAvailable = presence.status === 'available';
  const isPaused = presence.status === 'paused';

  const handlePause = (reason: PauseReason) => {
    send(buildPresencePause(reason));
  };

  const handleResume = () => {
    send(buildPresenceResume());
  };

  if (!isConnected) return null;

  return (
    <div className="flex items-center gap-2">
      <Badge
        variant={
          presence.status === 'available'
            ? 'success'
            : presence.status === 'paused'
              ? 'warning'
              : 'neutral'
        }
        title={t('presence.status_title', { status: presence.status })}
      >
        {presence.status === 'available' && t('presence.available')}
        {presence.status === 'paused' &&
          (presence.pauseReason
            ? t('presence.paused_reason', { reason: presence.pauseReason })
            : t('presence.paused'))}
        {presence.status === 'registering' && t('presence.registering')}
        {presence.status === 'offline' && 'Offline'}
        {presence.status === 'busy' && t('presence.busy')}
      </Badge>
      {isAvailable && (
        <div className="flex gap-1" role="group" aria-label={t('presence.controls')}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handlePause('break')}
            title={t('presence.break')}
            aria-label={t('presence.start_break')}
          >
            {t('presence.break')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handlePause('manual')}
            title={t('presence.paused')}
            aria-label={t('presence.start_pause')}
          >
            {t('presence.paused')}
          </Button>
        </div>
      )}
      {isPaused && (
        <Button
          variant="primary"
          size="sm"
          onClick={handleResume}
          aria-label={t('presence.resume_calls')}
        >
          {t('presence.resume')}
        </Button>
      )}
    </div>
  );
}
